import { Injectable } from '@angular/core';
import { Layer } from '../../models/layer';
import { WmsLayer } from '../../models/wms-layer';
import { WmtsLayer } from '../../models/wmts-layer';
import { VoidLayer } from '../../models/void-layer';
import { GeoJsonLayer } from '../../models/geo-json-layer';
import { MASKS } from '../../assets/mocks/masks';
import { MaskLayer } from '../../models/mask-layer';
import { OlLayerService } from '../ol/ol-layer.service';
import { Group as OlLayerGroup } from 'ol/layer';
import { AppEventService, PermalinkService } from '../permalink.service';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Observable, BehaviorSubject, Subject, ReplaySubject, throwError } from 'rxjs';
import { environment } from '../../../../../../projects/geobe/src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, filter, map, take } from 'rxjs/operators';
import { LayerAdapter } from '../../models/layer-adapter';
import { ToggleEventNameType, ToggleEventService, ToggleEventType } from '../../services/toggle-event.service';
import BaseLayer from 'ol/layer/Base';
import { MapUtilsService } from '../map-utils.service';


interface WmsCapabilitiesInfo {
  sublayers: string[];
  minScaleDenominator?: number;
  maxScaleDenominator?: number;
  queryable?: boolean;
}


/**
 * * Components call this class to do operations with the business layers
 * ! Don't call Openlayers methods directly in this class (use olLayerService instead)
 */

@Injectable({
  providedIn: 'root'
})
export class LayerService {

  // in order to work with model instances
  private layers$ = new BehaviorSubject<Layer[]>(new Array());
  private baselayers: Layer[] = new Array();
  private _baselayers$ = new BehaviorSubject<Layer[]>(new Array());

  private baseLayersSubject = new ReplaySubject<Layer[]>(1);
  public baseLayers$ = this.baseLayersSubject.asObservable();

  private selectedBaselayer: Layer;
  private selectedBaselayer$ = new Subject<Layer>();
  public activeBaselayerId$ = new Subject<string>();
  private maskLayer: MaskLayer;
  protected enabledLayers$ = new BehaviorSubject<Layer[]>(new Array());
  private currentLang: string;
  private baseLayersReadySubject = new BehaviorSubject<boolean>(false);
  public baseLayersReady$: Observable<boolean>;

  public activeBaselayerId: string;
  public activeSecondBaselayerId: string;


  constructor(
    protected olLayerService: OlLayerService,
    protected permalinkService: PermalinkService,
    protected appEventService: AppEventService,
    protected http: HttpClient,
    private translateService: TranslateService,
    protected layerAdapter: LayerAdapter,
    private toggleEventService: ToggleEventService,
    private mapUtilsService: MapUtilsService
  ) {
    this.currentLang = this.translateService.getCurrentLang();
    translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      if (typeof this.currentLang === 'undefined') {
        this.currentLang = translateService.getCurrentLang();
      }
      //prevent initlayerlist twice (on first load)
      if (event.lang !== this.currentLang) {
        this.currentLang = event.lang;
        this.initLayerList(event.lang).subscribe(() => { this.updateEnabledLayers(); this.updateBaseLayers() });
      }
    });
    this.baseLayersReady$ = this.baseLayersReadySubject.asObservable();
    //set opactiy in function of dark/light mode
    this.toggleEventService.emitter$.subscribe(
      (toggleEvent: ToggleEventType) => {
        if (toggleEvent.forwardedFor === 'theme-switch') {
          this.setCrossborderOpacity(toggleEvent.eventName);
        }
      }
    );
  }
  /*** PUBLIC METHODS ***/

  /*** GETTERS & SETTERS ***/

  getBaseLayers(): Layer[] {
    return this.baselayers;
  }

  getBaseLayersList(): BehaviorSubject<Layer[]> {
    return this._baselayers$;
  }

  getLayersList(): BehaviorSubject<Layer[]> {
    return this.layers$;
  }

  getMaskLayer(): MaskLayer {
    return this.maskLayer;
  }

  getEnabledLayers(): BehaviorSubject<Layer[]> {
    return this.enabledLayers$;
  }

  getLayerById(id: string) {
    return this.olLayerService.getLayerById(id);
  }
  getLayerOrGroupById(id: string) {
    return this.olLayerService.getLayerOrGroupById(id);
  }

  getEnabledLayersLength(): Observable<number> {
    return this.enabledLayers$.pipe(map((enabledLayers: Layer[]) => enabledLayers.length));
  }

  getDrawLayer(): BaseLayer {
    return this.olLayerService.getLayerById("drawLayer");
  }

  // * Needed when we change the language (see language-chooser component)
  updateEnabledLayers() {
    let enabledLayerArray = new Array();
    this.getLayersList().subscribe((layerList) => {
      layerList.forEach((layer) => {
        this.olLayerService.getOlEnabledLayers().forEach((olLayer) => {
          if (layer.id === olLayer.get("customId")) {
            enabledLayerArray.push(layer);
          }
        })
      })
    });
    this.enabledLayers$.next(enabledLayerArray);
  }

  getSelectedBaselayer(): Layer {
    return this.selectedBaselayer;
  }

  getSelectedBaselayer$(): Subject<Layer> {
    return this.selectedBaselayer$;
  }

  setSelectedBaselayer(layer: Layer): void {
    this.selectedBaselayer = layer;
    this.selectedBaselayer$.next(layer);
  }

  getActiveBaselayerId(): string {
    return this.activeBaselayerId;
  }

  getActiveBaselayerId$(): Subject<string> {
    return this.activeBaselayerId$;
  }

  setActiveBaselayerId$(layerId: string): void {
    this.activeBaselayerId = layerId;
    this.activeBaselayerId$.next(layerId);
  }

  setBaseLayers(baselayers: Layer[]): void {
    this.baselayers = baselayers;
    this._baselayers$.next(baselayers);
  }

  setLayersList(layers: Layer[]): void {
    this.layers$.next(layers);
  }

  setMaskLayer(mask: MaskLayer): void {
    this.maskLayer = mask;
  }

  getActiveSecondBaselayerId(): string {
    return this.activeSecondBaselayerId;
  }

  setActiveSecondBaselayerId(layerId: string): void {
    this.activeSecondBaselayerId = layerId;
  }


  /*** INIT ***/

  // init is made when the map comp is loaded, but if the first page isn't the map -> layers (json) are not loaded (we need them in the geobe catalog detail for example)
  // initLayerList(lang: string): Observable<void> {

  //   // initialisation of the available layers via a json file
  //   return this.getJSON(environment.layersPath + "/layers_" + lang + ".json").pipe(map((jsonLayers) => {
  //     const layerList: Layer[] = this.parseObjectsToLayers(this.fetchLayerListFromDataSource(jsonLayers));
  //     this.setLayersList(layerList);
  //   }))
  // }
  initLayerList(lang: string): Observable<Layer[]> {
    // initialisation of the available layers via a json file
    return this.getJSON(environment.layersPath + "/layers_" + lang + ".json").pipe(map((jsonLayers: any[]) => {
      this.populateJSONSubLayers(jsonLayers);
      let layers = this.objectToArray(jsonLayers);
      let layersArray: Layer[] = layers.map((layer: any) => this.layerAdapter.adapt(layer));

      this.setLayersList(layersArray.filter((layer) => layer != undefined));
      return layersArray;
    }))
  }

  // needed to replace sublayers references by "real" sublayers in layergroups
  protected populateJSONSubLayers(jsonLayers: any[]) {
    for (const [key, value] of Object.entries(jsonLayers)) {
      if (value.type === 'layergroup') {
        let subLayers: any = {};
        value.layers.forEach((subLayerId: string) => {
          subLayers[subLayerId] = jsonLayers[subLayerId];
        });
        value.layers = this.objectToArray(subLayers);
      }
    }
  }

  objectToArray(jsonLayers: any[]): any[] {
    let arr = [];
    Object.keys(jsonLayers).map(function (key) {
      arr.push({ [key]: jsonLayers[key] })
    });
    return arr;
  }

  initLayers() {
    let layers: string;
    let opacities: string;
    let visibilities: string;

    // universal link Q&D
    // this.appEventService.universalLink$
    //   .pipe(filter(params => !!params))
    //   .subscribe(param => {
    //     // console.log('*** universal link *** layerservice', param);
    //     if (param["layers"] != undefined || param["layers_opacity"] != undefined || param["layers_visibility"] != undefined) {
    //       layers = param["layers"];
    //       opacities = param["layers_opacity"];
    //       visibilities = param["layers_visibility"];
    //     }
    //   })


    //Normal startup params
    if (!this.appEventService.universalLink$.value) {
      this.permalinkService.getParams().pipe(take(1)).subscribe(param => {
        if (param["layers"] != undefined || param["layers_opacity"] != undefined || param["layers_visibility"] != undefined) {
          layers = param["layers"];
          opacities = param["layers_opacity"];
          visibilities = param["layers_visibility"];
        }
      })
    }


    // this.permalinkService.getParams().pipe(take(1)).subscribe((param) => {
    //   if (param["layers"] != undefined || param["layers_opacity"] != undefined || param["layers_visibility"] != undefined) {
    //     layers = param["layers"];
    //     opacities = param["layers_opacity"];
    //     visibilities = param["layers_visibility"];
    //   }
    // });

    if (layers != undefined || opacities != undefined || visibilities != undefined) {            
      let layerStringArray: string[] = layers.split(",");
      let opacityStringArray: string[] = opacities?.split(",");
      let visibilityStringArray: string[] = visibilities?.split(",");
      layerStringArray.forEach((layerId, index) => {
        layerId = decodeURIComponent(layerId)
        let layerToAdd: Layer;
        this.getLayersList().pipe(take(1)).subscribe((list) => {
          layerToAdd = list.find((layer) => {
            return layerId === layer.id
          })
          if (layerToAdd != undefined) {
            this.addLayer(layerToAdd);
            this.setOpacity(layerToAdd, Number(opacityStringArray[index]));
            this.setVisibility(layerToAdd, visibilityStringArray[index] === 'true'); // it's a string, so we need to do this to get a boolean value
          }
        });
      });

      this.handleViewServicesFromLocalStorage(opacityStringArray, visibilityStringArray);
    }
  }

  initBaseLayers(baselayersIds: Array<string>, addVoidLayer: boolean): void {
    const baselayers: Layer[] = [];
    baselayersIds.forEach(id => {
      baselayers.push(
        this.layers$.getValue().find(obj => {
          if (obj && obj.id) {
            return obj.id === id
          }
          return;
        }));
    })
    //add 'no background' layer
    if (addVoidLayer) baselayers.push(new VoidLayer('voidlayer', this.translateService.instant('base-layer-selector.no-baselayer'), ''));
    this.setBaseLayers(baselayers);
    this.olLayerService.addBaselayers(baselayers);
    let baselayerParam: any;
    this.permalinkService.getParams().pipe(take(1)).subscribe((param) => baselayerParam = param["baselayer"]);
    //the selected layer's the first element of the list if we don't have a value from url params (permalink);
    if (baselayerParam === undefined) {
      this.showSelectedBaselayer(this.baselayers[0]);
    } else {
      this.showSelectedBaselayer(this.getBaseLayers().find((layer) => {
        return layer.id.toLowerCase() === decodeURIComponent(baselayerParam);
      }) || this.baselayers[0]);
    }

    this.baseLayersSubject.next(baselayers);

    this.baseLayersReadySubject.next(true);

    // const baselayers: Layer[] = [];

    // baselayersIds.forEach(id => {
    //   baselayers.push(
    //     this.layers$.getValue().find(obj => obj && obj.id === id)!
    //   );
    // });

    // if (addVoidLayer) {
    //   baselayers.push(
    //     new VoidLayer(
    //       'voidlayer',
    //       this.translateService.instant('base-layer-selector.no-baselayer'),
    //       ''
    //     )
    //   );
    // }

    // this.setBaseLayers(baselayers);
    // this.olLayerService.addBaselayers(baselayers);

    // 
    // this.permalinkService.getParams()
    //   .pipe(take(1))
    //   .subscribe(params => {
    //     const baselayerParam = params['baselayer'];

    //     if (baselayerParam === undefined) {
    //       this.showSelectedBaselayer(this.baselayers[0]);
    //     } else {
    //       const fromPermalink = this.getBaseLayers().find(layer =>
    //         layer.id.toLowerCase() === decodeURIComponent(baselayerParam)
    //       );
    //       this.showSelectedBaselayer(fromPermalink || this.baselayers[0]);
    //     }

    //     //
    //     this.baseLayersReadySubject.next(true);
    //   });

  }

  setCrossborderOpacity(eventName: ToggleEventNameType) {
    const crossborderLayer = this.getLayerById('ngi.cartoweb.crossborder.be');
    const crossborderLayer_bw = this.getLayerById('ngi.cartoweb.crossborder_bw.be');    
    if (eventName === ToggleEventNameType.DARK_THEME) {
      crossborderLayer?.setOpacity(0.9);
      crossborderLayer_bw?.setOpacity(0.8);
    }
    else {
      crossborderLayer?.setOpacity(0.6);
      crossborderLayer_bw?.setOpacity(0.5);
    }

    //set the tilefunctin for the overlay
    this.olLayerService.getOlBaselayers().forEach((lyrOrGrp: any) => {
      //it's in a group
      if (lyrOrGrp instanceof OlLayerGroup) {
        lyrOrGrp.getLayers().forEach(lyr => {
          if (lyr.get('customId') === 'crossborder.notiles.overlay') {
            const source = (lyr as any).getSource();
            if (eventName === ToggleEventNameType.DARK_THEME) {
              source.setTileUrlFunction(() => './assets/images/darktile.png');
            }
            else {
              source.setTileUrlFunction(() => './assets/images/lighttile.png');
            }
            source.clear();
            source.setKey(Date.now().toString());
            source.refresh();
          }
        });
      }

    })
  }

  //After lang change
  updateBaseLayers() {
    let baselayersIds = this.getBaseLayers().map(layer => layer.id);
    const baselayers: Layer[] = [];
    baselayersIds.forEach(id => {
      if (id === 'voidlayer') {
        baselayers.push(new VoidLayer('voidlayer', this.translateService.instant('base-layer-selector.no-baselayer'), ''))
      } else {
        baselayers.push(
          this.layers$.getValue().find(obj => {
            return obj.id === id;
          }));
      }
    });
    this.setBaseLayers(baselayers);
  }

  initMaskLayer(): void {

    // ? to be defined in a config file ? 
    //const mask: MaskLayer = this.parseObjectToLayer({ id: "circleMask", properties: this.fetchMaskFromDataSource() }) as MaskLayer;
    //this.setMaskLayer(mask);
    //this.olLayerService.addMasklayers([mask]);
    //this.olLayerService.addMaskLayerToMap(this.maskLayer); // for now we're adding by default the mask, comment this to remove it
  }

  /*** OPERATIONS ***/

  // When a client adds a layer he doesn't need to know if it's an update or an simple add ... 
  addLayer(layer: Layer, skipPermalinkUpdate = false): void {
    if (layer != undefined) {
      if (!this.isAlreadyAddedToTheMap(layer.id)) {        
        this.olLayerService.setZIndex(layer, this.enabledLayers$.value.length);
        this.enabledLayers$.value.push(layer);
        this.enabledLayers$.next(this.enabledLayers$.value);
      }
      this.olLayerService.addLayerToMap(layer, "layers");

      if (!skipPermalinkUpdate) {
        this.updateLayersPermalink();
      }
    }
  }
  
  addLayertest(layer: any, skipPermalinkUpdate = false): void {
    if (layer != undefined) {

      this.olLayerService.addLayerToMap(layer, "layers");

      if (!skipPermalinkUpdate) {
        this.updateLayersPermalink();
      }
    }
  }

  isAlreadyAddedToTheMap(layerId: string): boolean {
    return this.olLayerService.findEnabledOlLayer(layerId) != undefined;
  }

  findEnabledLayerById(layerId: string): Observable<Layer> {
    return this.getEnabledLayers().pipe(
      map((lyrs) => lyrs.find(lyr => lyr.id == layerId))
    )
  }

  removeLayer(layer: Layer): void {

    // if it's a wms, we reset the enabled property to true for all the subLayers
    if (layer.type === "wms") {
      (layer as WmsLayer).sublayers.forEach((sublayer: any) => sublayer.enabled = true);
      // if it's inside the localStorage we remove it
      this.removeFromLocalStorage(layer);

    }

    this.olLayerService.removeLayerFromMap(layer);

    this.enabledLayers$.next(this.enabledLayers$.value.filter((lyr) => layer.id !== lyr.id));
    this.updateLayersPermalink();
  }

  removeLayerById(id: string): void {
    const layer = this.enabledLayers$.value.find(l => l.id === id);
    if (!layer) return;
    this.removeLayer(layer);
  }

  updateLayersPermalink() {
    let layersString: string[] = new Array<string>();
    let opacitiesString: string[] = new Array<string>();
    let visibilitiesString: string[] = new Array<string>();

    this.getEnabledLayers().subscribe((enabledLayers) => {
      enabledLayers.forEach((lyr) => {
        layersString.push(lyr.id.toLocaleLowerCase());
        opacitiesString.push(lyr.opacity.toString());
        visibilitiesString.push(lyr.visible.toString());
      });
    })

    //pve
    this.permalinkService.updateLayersParam(layersString, opacitiesString, visibilitiesString)
  }

  setOpacity(layer: Layer, opacity: number) {
    layer.opacity = opacity;
    this.olLayerService.setOpacity(layer, opacity);
    this.updateLayersPermalink();
  }

  setVisibility(layer: Layer, visible: boolean) {
    layer.visible = visible;
    this.olLayerService.setVisible(layer, visible);
    this.updateLayersPermalink();
  }

  showSelectedBaselayer(currentLayer: Layer) {
    this.setSelectedBaselayer(currentLayer);
    this.olLayerService.displaySelectedBaselayer(currentLayer);
    this.permalinkService.updateBaseLayerParam(currentLayer);
  }

  swap(layer: Layer, targetedIndex: number) {

    const layerIndex = this.enabledLayers$.value.indexOf(layer);

    // swap place 
    let tmp = this.enabledLayers$.value[layerIndex];
    this.enabledLayers$.value[layerIndex] = this.enabledLayers$.value[targetedIndex];
    this.enabledLayers$.value[targetedIndex] = tmp;

    // modify zIndex
    this.olLayerService.setZIndex(this.enabledLayers$.value[layerIndex], layerIndex);
    this.olLayerService.setZIndex(this.enabledLayers$.value[targetedIndex], targetedIndex);

    this.enabledLayers$.next(this.enabledLayers$.value);
    this.updateLayersPermalink();
  }

  findAvailableBusinessLayerById(bodId: string): Observable<Layer> {
    return this.getLayersList().pipe(
      take(1),
      map(list => {
        const layer = list.find(l => l.id === bodId);
        if (!layer) {
          throw new Error(`Layer ${bodId} not found`);
        }
        return layer;
      })
    );
  }


// findAvailableBusinessLayerById(bodId: string): Observable<Layer> {
//   return this.getLayersList().pipe(
//     map(list => list.find(l => l.id === bodId)),
//     filter((layer): layer is Layer => !!layer),
//     take(1)
//   );
// }


  findAvailableBusinessLayerByUUID(uuid: string): Observable<Layer> {
    return this.getLayersList().pipe(
      map((list) => {
        const layer = list.find(
          (layer) =>
            layer instanceof WmsLayer &&
            layer.id === uuid
        );

        if (!layer) {
          throw new Error(`Layer not found for UUID: ${uuid}`);
        }

        return layer;
      })
    );
  }


  getEnabledLayersIds(): Observable<string[]> {

    return this.enabledLayers$.pipe(
      map((layers) => {
        return layers.map(layer => layer.id);
      }
      )
    )
  }

  // is it the best place to do this ?
  loadServiceFromCapabilities(
    uuid: string,
    url: string,
    title: string,
    epsgCode: string = '3812',
    layerNames?: string[],
  ): Observable<WmsLayer> {

    const supportedFormats: string[] = ['wms', 'wmts'];

    const serviceType =
      new URL(url).searchParams.get('service')?.toLowerCase();

    if (
      serviceType === undefined ||
      !supportedFormats.includes(serviceType)
    ) {
      return throwError(
        () => new Error('Service type not supported : ' + serviceType)
      );
    }

    if (serviceType === 'wms') {

      return this.http.get(url, { responseType: 'text' }).pipe(

        map(xml => this.parseWmsCapabilities(xml)),

        map((capabilities: WmsCapabilitiesInfo) => {

          let sublayers = capabilities.sublayers;

          if (layerNames?.length) {

            const allowed = new Set(layerNames);

            sublayers = sublayers.filter(layer =>
              allowed.has(layer)
            );
          }

          if (!sublayers.length) {
            throw new Error(
              'No matching sublayers found in WMS capabilities'
            );
          }

          const wmsLayer = new WmsLayer(
            uuid,
            title,
            serviceType,
            url,
            epsgCode,
            sublayers.join(','),
            undefined,
            capabilities.minScaleDenominator,
            capabilities.maxScaleDenominator,
            false,
            false,
            undefined,
            false,
            capabilities.queryable
          );
          return wmsLayer;
        }),

        catchError(() =>
          throwError(
            () => new Error('Service unavailable')
          )
        )
      );
    }

    return throwError(
      () => new Error(`The service '${url}' cannot be added`)
    );
  }


  /**** PRIVATE METHODS ****/

  /*** FETCH DATA ***/

  private fetchMaskFromDataSource(): any[] {
    // this is a temporary static import for test
    return MASKS[0];
  }

  /*** PARSING ***/
  //todo add epsg to wmts
  private parseObjectToLayer(layerNotTyped: any): Layer {

    let layer: Layer;
    if (layerNotTyped.properties.type === "wms") {
      layer = new WmsLayer(layerNotTyped.id, layerNotTyped.properties.label, layerNotTyped.properties.type, layerNotTyped.properties.wmsUrl, layerNotTyped.properties.wmsLayers, layerNotTyped.properties.format, layerNotTyped.properties.minScaleDenominator, layerNotTyped.properties.maxScaleDenominator, layerNotTyped.properties.base, layerNotTyped.properties.singleTile);
    } else if (layerNotTyped.properties.type === "wmts") {
      layer = new WmtsLayer(layerNotTyped.id, layerNotTyped.properties.metadataId, layerNotTyped.properties.layerName, layerNotTyped.properties.label, layerNotTyped.properties.type, layerNotTyped.properties.wmtsUrl, layerNotTyped.properties.format, layerNotTyped.properties.base, layerNotTyped.properties.singleTile, layerNotTyped.properties.tilegridExtent, layerNotTyped.properties.tilegridLevels, 'EPSG:3812', layerNotTyped.properties.notilesUrl);
      (layer as WmtsLayer).updateHasWebGlSupport(this.mapUtilsService.hasWebGlSupport);
    } else if (layerNotTyped.properties.type === "GeoJSON") {
      layer = new GeoJsonLayer(layerNotTyped.id, layerNotTyped.properties.label, layerNotTyped.properties.path, layerNotTyped.properties.type);
    } else if (layerNotTyped.properties.type === "mask") {
      layer = new MaskLayer(layerNotTyped.id, layerNotTyped.properties.label, layerNotTyped.properties.type, layerNotTyped.properties.projection, layerNotTyped.properties.maskType, layerNotTyped.properties.coords);
    } else {
      return undefined;
    }
    // * bind legend to layer
    if (layer) {
      layer.setLegend();
      return layer;
    }
  }

  // Not used yet
  // getCapabilities(layer: Layer): Observable<any> {
  //   return this.http.get("geobe-services/GetWmsCapabilities?serviceType=WMS&serviceUrl=" + encodeURIComponent(layer.url) + "&serviceVersion=1.3.0");
  // }

  // TODO: use the adapter pattern
  protected getJSON(url): Observable<any> {
    let headers = new HttpHeaders({
      //'Cache-Control': 'no-cache',
      //'Pragma': 'no-cache',
      'Content-Type': 'application/json',
    })
    return this.http.get(url
      ,
      {
        headers: headers
      }
    );
  }

  protected handleViewServicesFromLocalStorage(opacityStringArray: string[], visibilityStringArray: string[]) {

    let viewServicesFromLocalStorage: string = localStorage.getItem("wmsLayers");
    if (viewServicesFromLocalStorage && viewServicesFromLocalStorage.length > 0) {
      let viewServices: Layer[] = new Array();
      viewServices = JSON.parse(viewServicesFromLocalStorage);
      viewServices.forEach((viewService: WmsLayer, index) => {
        let layerToAdd = new WmsLayer(viewService.id, viewService.label, viewService.type, viewService.url, null, viewService.layerName,
          viewService.format, viewService.minScaleDenominator, viewService.maxScaleDenominator, viewService.base, viewService.singleTile);

        this.addLayer(layerToAdd);
        this.setOpacity(layerToAdd, Number(opacityStringArray[index]));
        this.setVisibility(layerToAdd, visibilityStringArray[index] === 'true');
      });
    }
  }

  protected removeFromLocalStorage(layer: Layer) {
    let viewServicesCollection: any[] = JSON.parse(localStorage.getItem("wmsLayers"));

    viewServicesCollection?.forEach((viewService, index) => {
      if (viewService.id === layer.id) {
        viewServicesCollection.splice(index, 1);
        localStorage.setItem("wmsLayers", JSON.stringify(viewServicesCollection));
      }
    })
  }

  protected parseWmsCapabilities(xml: string): WmsCapabilitiesInfo {

    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');

    // Check for errors
    const serviceException =
      doc.getElementsByTagName('ServiceException')[0];

    if (serviceException) {
      throw new Error(
        serviceException.textContent?.trim() || 'WMS ServiceException'
      );
    }

    const isQueryable = doc.querySelector('Layer[queryable="1"]') !== null;

    const sublayers: string[] = [];

    let globalMinScale: number | undefined;
    let globalMaxScale: number | undefined;

    const layerNodes = Array.from(doc.getElementsByTagName('Layer'));

    layerNodes.forEach(layerNode => {

      const hasChildLayers = Array.from(layerNode.children)
        .some(child => child.tagName === 'Layer');

      if (hasChildLayers) {
        return;
      }

      const name = Array.from(layerNode.children)
        .find(child => child.tagName === 'Name')
        ?.textContent
        ?.trim();

      if (!name) {
        return;
      }

      sublayers.push(name);

      const minScale = Number(
        Array.from(layerNode.children)
          .find(child => child.tagName === 'MinScaleDenominator')
          ?.textContent
      );

      const maxScale = Number(
        Array.from(layerNode.children)
          .find(child => child.tagName === 'MaxScaleDenominator')
          ?.textContent
      );

      if (!isNaN(minScale)) {
        globalMinScale =
          globalMinScale === undefined
            ? minScale
            : Math.min(globalMinScale, minScale);
      }

      if (!isNaN(maxScale)) {
        globalMaxScale =
          globalMaxScale === undefined
            ? maxScale
            : Math.max(globalMaxScale, maxScale);
      }
    });

    return {
      sublayers,
      minScaleDenominator: globalMinScale,
      maxScaleDenominator: globalMaxScale,
      queryable: isQueryable
    };
  }
}
